import {
	CustomerOrderInfoViewApi,
	CustomerOrderInfoViewApi_CustomerOrderInfoViewData_MainDocument,
} from "src/api/generated/erp/sales/customerOrder/api/customerOrderInfoViewApi";
import { AsyncFetchRender } from "src/components/common/async/AsyncFetchRender";
import { HorizontalBox } from "src/components/common/box/HorizontalBox";
import { VerticalBox } from "src/components/common/box/VerticalBox";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { DocumentPreviewView } from "src/components/views/documents/preview/DocumentPreviewView.tsx";
import { Box, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faLocationDot, faPhone, faUser } from "@fortawesome/pro-regular-svg-icons";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface CustomerOrderInfoViewProps {
	customerOrderId: number;
}

export const CustomerOrderInfoView = ({ customerOrderId }: CustomerOrderInfoViewProps) => {
	const { openDialog } = useGenericDialog();

	return (
		<AsyncFetchRender
			fetch={() => CustomerOrderInfoViewApi.getCustomerOrderInfoViewData({ customerOrderId })}
			content={({
				mainDocument,
				deliveryAddress_1,
				deliveryCity,
				deliveryPostalCode,
				contactName,
				contactPhone,
				contactEmail,
			}) => (
				<Box
					sx={{
						display: "flex",
						flexDirection: {
							xs: "column",
							sm: "row",
						},
						flex: 1,
						margin: 1.5,
						gap: 2,
						overflow: "auto",
					}}
				>
					<VerticalBox
						sx={{
							flexShrink: 0,
							gap: 1,
							backgroundColor: "grey.50",
							borderRadius: 2,
							padding: 2,
							border: "1px solid",
							borderColor: "divider",
						}}
					>
						{contactName && (
							<HorizontalBox sx={{ alignItems: "center", gap: 1.5 }}>
								<FontAwesomeIcon icon={faUser} style={{ opacity: 0.5, width: 16 }} />
								<Typography variant="body2" sx={{ fontWeight: 500 }}>
									{contactName}
								</Typography>
							</HorizontalBox>
						)}
						{contactPhone && (
							<HorizontalBox sx={{ alignItems: "center", gap: 1.5 }}>
								<FontAwesomeIcon icon={faPhone} style={{ opacity: 0.5, width: 16 }} />
								<Typography variant="body2" color="text.secondary">
									{contactPhone}
								</Typography>
							</HorizontalBox>
						)}
						{contactEmail && (
							<HorizontalBox sx={{ alignItems: "center", gap: 1.5 }}>
								<FontAwesomeIcon icon={faEnvelope} style={{ opacity: 0.5, width: 16 }} />
								<Typography variant="body2" color="text.secondary">
									{contactEmail}
								</Typography>
							</HorizontalBox>
						)}
						{(deliveryAddress_1 || deliveryCity) && (
							<HorizontalBox sx={{ alignItems: "flex-start", gap: 1.5, mt: 0.5 }}>
								<FontAwesomeIcon
									icon={faLocationDot}
									style={{ opacity: 0.5, width: 16, marginTop: 3 }}
								/>
								<VerticalBox>
									{deliveryAddress_1 && (
										<Typography variant="body2" color="text.secondary">
											{deliveryAddress_1}
										</Typography>
									)}
									<Typography variant="body2" color="text.secondary">
										{deliveryPostalCode} {deliveryCity}
									</Typography>
								</VerticalBox>
							</HorizontalBox>
						)}
					</VerticalBox>
					{mainDocument && (
						<HorizontalBox
							sx={{
								overflow: "auto",
								flex: 1,
								borderRadius: 2,
								border: "1px solid",
								borderColor: "divider",
								backgroundColor: "grey.50",
							}}
						>
							<img
								src={mainDocument.fileHandle.fileUrl}
								alt="image"
								style={{
									marginLeft: "auto",
									marginRight: "auto",
									cursor: "pointer",
									borderRadius: 4,
								}}
								onClick={() => {
									openDocument(mainDocument);
								}}
							/>
						</HorizontalBox>
					)}
				</Box>
			)}
		/>
	);

	function openDocument(document: CustomerOrderInfoViewApi_CustomerOrderInfoViewData_MainDocument) {
		openDialog(() => ({
			title: document.description,
			agentContext: {
				key: defineAgentUiContextKey("erp.sales.customerOrderMainDocument.dialog"),
				pathSegments: [document.description],
				data: {
					customerOrderId,
					documentId: document.documentId,
				},
			},
			content: <DocumentPreviewView documentId={document.documentId} />,
		}));
	}
};
